import React from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useSet } from "react-use";
import { formatTimeToAmPm, toDayMonthNameFormat } from "@/helpers/dates";
import { BusinessReport, BusinessReportPrompt } from "@/modules/snapshots/businessReports.types";
import { BusinessReportDetailsRow } from "../generate/businessReportDetailsRow";

interface Props {
  businessReport: BusinessReport;
  prompts: BusinessReportPrompt[];
  setPrompts: React.Dispatch<React.SetStateAction<BusinessReportPrompt[]>>;
}

const formatDateTime = (dateString: string) => (
  <div className="space-y-1">
    <div className="text-sm text-gray-600">{toDayMonthNameFormat(dateString)}</div>
    <div className="text-sm text-desert-sand-600">{formatTimeToAmPm(dateString)}</div>
  </div>
);

export const BusinessReportDetails: React.FC<Props> = ({ setPrompts, prompts, businessReport }) => {
  const [expandedItems, { toggle, clear, add }] = useSet<string>(new Set());

  const isAllExpanded = prompts.every(p => expandedItems.has(p.id));

  const toggleAll = () => {
    if (isAllExpanded) {
      clear();
    } else {
      prompts.forEach(p => add(p.id));
    }
  };

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-bold">
          {businessReport.name}
          {!!businessReport.variables.length && (
            <span className="text-medium text-sm font-normal text-gray-600"> - Variables are required</span>
          )}
        </h2>
      </div>

      <div className="grid gap-6 rounded-lg bg-gray-50 p-6">
        <div>
          <h3 className="mb-2 text-lg font-semibold">Created</h3>
          {formatDateTime(businessReport.created_at)}
        </div>

        {businessReport.last_used_at && (
          <div className="border-t border-gray-200 pt-6">
            <h3 className="mb-2 text-lg font-semibold">Last Used</h3>
            {formatDateTime(businessReport.last_used_at)}
          </div>
        )}
      </div>

      <div className="mb-6 mt-6 flex items-center justify-between">
        <h3 className="text-lg font-semibold">Prompts</h3>
        <button
          onClick={toggleAll}
          className="bg-desert-sand-50 inline-flex items-center gap-2 rounded-md border border-desert-sand-200 px-3 py-1.5 text-sm font-medium text-desert-sand-600 transition-all hover:border-desert-sand-300 hover:bg-desert-sand-100"
        >
          {isAllExpanded ? (
            <>
              <ChevronUp size={16} className="text-desert-sand-500" />
              <span>Collapse all</span>
            </>
          ) : (
            <>
              <ChevronDown size={16} className="text-desert-sand-500" />
              <span>Expand all</span>
            </>
          )}
        </button>
      </div>

      {prompts.map(prompt => (
        <BusinessReportDetailsRow
          key={prompt.id}
          prompt={prompt}
          setPrompts={setPrompts}
          isExpanded={expandedItems.has(prompt.id)}
          onToggle={() => toggle(prompt.id)}
        />
      ))}
    </>
  );
};
